import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AppTable from 'components/Shared/AppTable';
import AppTableDateTime from 'components/Shared/AppTableDateTime';
import BreadcrumbLayout from 'layouts/BreadcrumbLayout';
import { useTablePagination } from 'hooks';
import { useLazyListContractorPricesQuery } from "store/services/contractorApi";

const ContractorPriceList = () => {
  const { id } = useParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [paymentStatus, setPaymentStatus] = useState('All');

  const paymentStatusOptions = ['All', 'Paid', 'Not Paid'];

  const columns = [
    {
      name: 'Shipment ID',
      selector: (row) => row?.shipment?.shipmentId || 'N/A',
      cell: (row) => row?.shipment?.shipmentId || 'N/A',
      width: '150px',
    },
    {
      name: 'Fee Item',
      selector: (row) => row?.feeItem?.name || 'N/A',
      cell: (row) => row?.feeItem?.name || 'N/A',
    },
    {
      name: 'Charge',
      selector: (row) => row?.charge,
      cell: (row) => `Rs. ${row?.charge?.toFixed(2)}`,
    },
    {
      name: 'Charge Type',
      selector: (row) => row?.chargeType,
      center: true,
      cell: (row) => (
        <Badge
          className={`px-1 py-1 rounded-md ${row.chargeType ? '' : 'bg-gray-500 text-white'}`}
        >
          {row.chargeType || 'N/A'}
        </Badge>
      ),
    },
    {
      name: 'Payer',
      selector: (row) => row?.payer || 'N/A',
      cell: (row) => row?.payer || 'N/A',
    },
    {
      name: 'Payment Location',
      selector: (row) => row?.paymentLocation || 'N/A',
      cell: (row) => row?.paymentLocation || 'N/A',
    },
    {
      name: 'Status',
      selector: (row) => row?.paymentStatus || row?.discountStatus || row?.remitStatus || 'N/A',
      center: true,
      cell: (row) => {
        const status = row?.paymentStatus || row?.discountStatus || row?.remitStatus || 'N/A';

        return (
          <Badge
            className={`px-1 py-1 rounded-md ${status}`}
          >
            {status}
          </Badge>
        );
      },
    },
    {
      name: 'Created At',
      selector: (row) => row?.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
  ];

  const [fetchPrices, { data: priceRes, isLoading, isFetching }] =
    useLazyListContractorPricesQuery();

  useEffect(() => {
    if (id) {
      fetchPrices({
        contractorId: id,
        page: currentPage,
        limit: perPage,
        paymentStatus: paymentStatus !== 'All' ? paymentStatus : undefined,
      });
    }
  }, [id, fetchPrices, currentPage, perPage, paymentStatus]);

  return (
    <BreadcrumbLayout.Paper>
      <Box display="flex" justifyContent="flex-end" mb={1} mt={1}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Payment Status</InputLabel>
          <Select
            value={paymentStatus}
            label="Payment Status"
            onChange={(e) => {
              setPaymentStatus(e.target.value);
              handlePageChange(1); // Reset to first page on filter change
            }}
            size="small"
          >
            {paymentStatusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {priceRes ? (
        <AppTable
          columns={columns}
          data={priceRes?.data || []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={priceRes?.totalItems || 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <CircularProgress size={24} />
          <Typography variant="body2" marginLeft={1}>
            Loading Prices...
          </Typography>
        </Box>
      )}
    </BreadcrumbLayout.Paper>
  );
};

export default ContractorPriceList;